import { useMemo } from "react";
import type { PlaybookRun, PlaybookRunStep } from "../lib/types";
import { stepState, type StepState } from "../lib/schedule";
import { Icon } from "./icons";

/* A dependency-aware Gantt for a playbook plan. Bars are positioned by the CPM
 * schedule (earliest start + duration); slack extends as a lighter tail to the
 * latest finish; the critical path is highlighted; dependency arrows connect
 * predecessor finish → successor start; a marker shows "today". Steps are
 * clickable to complete (the parent decides what that means — usually routing
 * through the task's completion gate). */

const DAY_W = 26;          // px per day
const ROW_H = 34;
const LABEL_W = 230;
const HEAD_H = 26;

const stateColor: Record<StepState, string> = {
  done: "var(--good)", eligible: "var(--corona)", blocked: "var(--faint)", skipped: "var(--line)",
};

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

export function Gantt({ run, today = "2026-08-27", onOpenStep }: {
  run: PlaybookRun; today?: string; onOpenStep?: (step: PlaybookRunStep) => void;
}) {
  const canOpen = (st: StepState) => !!onOpenStep && st !== "skipped";
  const start = run.planStartDate ?? run.startedAt.slice(0, 10);
  const byId = useMemo(() => new Map(run.steps.map((s) => [s.id, s])), [run.steps]);
  const totalDays = Math.max(1, ...run.steps.map((s) => s.latestFinish), ...run.steps.map((s) => s.earliestFinish));
  const chartW = (totalDays + 1) * DAY_W;
  const rowY = (i: number) => HEAD_H + i * ROW_H + ROW_H / 2;
  const todayX = daysBetween(start, today) * DAY_W;

  // week gridlines
  const weeks = Array.from({ length: Math.ceil((totalDays + 1) / 7) + 1 }, (_, w) => w * 7);

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: "var(--r)", background: "var(--surface)" }}>
      <div style={{ display: "flex", minWidth: LABEL_W + chartW }}>
        {/* left: labels */}
        <div style={{ width: LABEL_W, flexShrink: 0, borderRight: "1px solid var(--line)" }}>
          <div style={{ height: HEAD_H, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 12px", fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--faint)" }}>Step</div>
          {run.steps.map((s) => {
            const st = stepState(s, byId);
            const open = canOpen(st);
            return (
              <div key={s.id} onClick={open ? () => onOpenStep!(s) : undefined}
                title={open ? "Open step" : undefined}
                style={{ height: ROW_H, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", borderBottom: "1px solid var(--line-soft)", cursor: open ? "pointer" : "default" }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: stateColor[st], flexShrink: 0 }} title={st} />
                <span style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: st === "skipped" ? "var(--faint)" : "var(--ink)", textDecoration: open ? "underline" : "none", textDecorationColor: "var(--line)", textUnderlineOffset: 2 }}>{s.label}</span>
                {s.critical && st !== "done" && <span className="tag" style={{ marginLeft: "auto", color: "var(--risk)", borderColor: "rgba(187,71,44,.3)", padding: "0 5px", fontSize: 9.5 }}>CP</span>}
              </div>
            );
          })}
        </div>

        {/* right: timeline */}
        <div style={{ position: "relative", width: chartW, flexShrink: 0 }}>
          {/* week gridlines + axis */}
          <div style={{ height: HEAD_H, borderBottom: "1px solid var(--line)", position: "relative" }}>
            {weeks.map((d) => (
              <span key={d} style={{ position: "absolute", left: d * DAY_W, top: 0, height: HEAD_H, display: "flex", alignItems: "center", paddingLeft: 4, fontFamily: "var(--mono)", fontSize: 10, color: "var(--faint)" }}>W{d / 7 + 1}</span>
            ))}
          </div>
          <div style={{ position: "absolute", inset: `${HEAD_H}px 0 0 0` }}>
            {weeks.map((d) => <div key={d} style={{ position: "absolute", left: d * DAY_W, top: 0, bottom: 0, width: 1, background: "var(--line-soft)" }} />)}
          </div>

          {/* dependency arrows */}
          <svg style={{ position: "absolute", inset: 0, width: chartW, height: HEAD_H + run.steps.length * ROW_H, pointerEvents: "none", overflow: "visible" }}>
            {run.steps.flatMap((s, i) => s.dependsOn.map((dep) => {
              const p = byId.get(dep); if (!p) return null;
              const pi = run.steps.indexOf(p);
              const x1 = p.earliestFinish * DAY_W, y1 = rowY(pi);
              const x2 = s.earliestStart * DAY_W, y2 = rowY(i);
              const midX = Math.max(x1 + 8, x2 - 8);
              return <path key={`${dep}-${s.id}`} d={`M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`} fill="none" stroke="var(--line)" strokeWidth={1.5} markerEnd="url(#arrow)" />;
            }))}
            <defs><marker id="arrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><path d="M0 0 L6 3.5 L0 7 z" fill="var(--faint)" /></marker></defs>
          </svg>

          {/* today marker */}
          {todayX >= 0 && todayX <= chartW && (
            <div style={{ position: "absolute", left: todayX, top: HEAD_H, bottom: 0, width: 2, background: "var(--corona)", opacity: .5 }} title={`Today · ${today}`} />
          )}

          {/* bars */}
          {run.steps.map((s, i) => {
            const st = stepState(s, byId);
            const left = s.earliestStart * DAY_W;
            const width = Math.max(DAY_W * 0.6, s.durationDays * DAY_W);
            const slackW = s.slack * DAY_W;
            const clickable = canOpen(st);
            const solid = st === "done" || st === "eligible" || (s.critical && st !== "skipped" && st !== "blocked");
            return (
              <div key={s.id} style={{ position: "absolute", top: HEAD_H + i * ROW_H, height: ROW_H, left: 0, right: 0 }}>
                {/* slack tail */}
                {slackW > 0 && st !== "done" && <div style={{ position: "absolute", left: left + width, top: ROW_H / 2 - 3, width: slackW, height: 6, background: "var(--line)", borderRadius: 3, opacity: .5 }} title={`${s.slack}d slack`} />}
                <div
                  onClick={clickable ? () => onOpenStep!(s) : undefined}
                  title={`${s.label} · ${s.startDate} → ${s.dueDate}${s.slack ? ` · ${s.slack}d slack` : " · critical"}`}
                  style={{
                    position: "absolute", left, top: ROW_H / 2 - 9, width, height: 18, borderRadius: 5,
                    background: st === "blocked" ? "repeating-linear-gradient(45deg,var(--surface-2),var(--surface-2) 4px,var(--line) 4px,var(--line) 6px)"
                      : st === "skipped" ? "var(--surface-2)"
                      : s.critical && st !== "done" ? "var(--risk)" : stateColor[st],
                    border: s.critical && st !== "done" ? "1px solid var(--risk)" : "1px solid transparent",
                    display: "flex", alignItems: "center", gap: 4, padding: "0 6px", cursor: clickable ? "pointer" : "default",
                    boxShadow: clickable ? "0 1px 3px rgba(0,0,0,.12)" : "none",
                  }}
                >
                  {st === "done" && <Icon name="check" size={11} className="" />}
                  {st === "blocked" && <Icon name="lock" size={10} className="" />}
                  <span style={{ fontSize: 10.5, color: solid ? "#fff" : "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.durationDays}d</span>
                </div>
              </div>
            );
          })}
          <div style={{ height: run.steps.length * ROW_H }} />
        </div>
      </div>
    </div>
  );
}
